import { PublicConfig } from "apps/website/config/public";
import { PRODUCTION_URLS } from "apps/website/constants";
import { FIRST_PARTY_DOMAIN } from "apps/website/constants/website";

export const isLocalPath = (path: string) => path.startsWith("/") && !path.startsWith("/manage-plan");

// Get the root path of a page, e.g. /products/cluck, the root path would be /products
export const getLocalPathRootPage = (path: string, basePath: string | undefined) => {
  let pathToReturn = (basePath && path.startsWith(basePath)) ? path.replace(basePath, "").split("?")[0] : path.split("?")[0];
  pathToReturn = pathToReturn?.split("#")[0];
  return pathToReturn?.split("/")[1];
};

export const isActivePath = (linkPath: string, currentPath: string, basePath: string | undefined) => {
  if (!isLocalPath(linkPath)) return false;
  if (getLocalPathRootPage(linkPath, basePath) === getLocalPathRootPage(currentPath, basePath)) return true;
  return false;
};

// A relative href is only safe if it resolves back to this origin: anything that
// smuggles in an authority ("/\evil.example") resolves elsewhere and is rejected.
const URL_PARSE_ORIGIN = "https://internal-link.invalid";

const AUTHORITY_PREFIX = /^[/\\]{2}/;
const SCHEME_PREFIX = /^[a-z][a-z0-9+.-]*:/i;
const HTTP_PROTOCOLS = new Set([ "http:", "https:" ]);
const SEPARATE_FRONTEND_PREFIXES = [ "/manage-plan" ];

export type TLinkKind = "internal" | "firstParty" | "external" | "scheme";

export interface IResolvedLink {
  kind: TLinkKind;
  href: string;
}

interface ILinkParts {
  path: string;
  hostname?: string;
  protocol?: string;
  absoluteHref?: string;
}

const normaliseHostname = (hostname: string) => hostname.toLowerCase().replace(/\.$/, "");

const toHostname = (url: string) => {
  try {
    return normaliseHostname(new URL(url).hostname);
  } catch {
    return undefined;
  }
};

const INTERNAL_HOSTNAMES = new Set(
  [ PublicConfig.NEXT_PUBLIC_WEBSITE_URL, ...PRODUCTION_URLS ]
    .filter((url): url is string => !!url)
    .map((url) => toHostname(url))
    .filter((hostname): hostname is string => !!hostname),
);

const toHttps = (href: string) => href.replace(/^http:\/\//i, "https://");

const isFirstPartyHostname = (hostname: string) => hostname === FIRST_PARTY_DOMAIN
  || hostname.endsWith(`.${FIRST_PARTY_DOMAIN}`);

const parseRelativeHref = (href: string): ILinkParts | undefined => {
  try {
    // Returned verbatim, because rebuilding from URL parts turns "#anchor" into "/#anchor".
    return new URL(href, `${URL_PARSE_ORIGIN}/`).origin === URL_PARSE_ORIGIN ? { path: href } : undefined;
  } catch {
    return undefined;
  }
};

const parseAbsoluteHref = (href: string, hasAuthority: boolean): ILinkParts | undefined => {
  const absoluteHref = hasAuthority ? `https:${href.replace(AUTHORITY_PREFIX, "//")}` : href;
  try {
    const url = new URL(absoluteHref);
    return {
      path: `${url.pathname}${url.search}${url.hash}`,
      hostname: normaliseHostname(url.hostname),
      protocol: url.protocol.toLowerCase(),
      absoluteHref,
    };
  } catch {
    return undefined;
  }
};

const parseHref = (href: string): ILinkParts | undefined => {
  if (!href) {
    return { path: "" };
  }

  const hasAuthority = AUTHORITY_PREFIX.test(href);
  if (!hasAuthority && !SCHEME_PREFIX.test(href)) {
    return parseRelativeHref(href);
  }

  return parseAbsoluteHref(href, hasAuthority);
};

export const resolveLinkHref = (href?: string): IResolvedLink => {
  const value = (href ?? "").trim();
  const parts = parseHref(value);

  if (!parts) {
    return { kind: "external", href: value };
  }

  if (parts.protocol && !HTTP_PROTOCOLS.has(parts.protocol)) {
    return { kind: "scheme", href: value };
  }

  if (parts.hostname && !INTERNAL_HOSTNAMES.has(parts.hostname)) {
    return isFirstPartyHostname(parts.hostname)
      ? { kind: "firstParty", href: toHttps(parts.absoluteHref ?? value) }
      : { kind: "external", href: value };
  }

  const isSeparateFrontend = SEPARATE_FRONTEND_PREFIXES.some((prefix) => parts.path.startsWith(prefix));

  return { kind: isSeparateFrontend ? "firstParty" : "internal", href: parts.path };
};
